import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  Output,
} from '@angular/core'
import { getSessionTitle, SessionWithState } from '@helios/state-management'
import { NgbTooltip, NgbTooltipConfig } from '@ng-bootstrap/ng-bootstrap'
import {
  DisplayDateTimeSecondFormat,
  formatDate,
  InputBoolean,
  ISODateFormat,
} from '@northfork/basics'
import { Subject } from 'rxjs'
import { SessionsService } from '../../services/sessions-service/sessions.service'

const ISODateTimeNanoFormat = "yyyy-MM-dd'T'HH:mm:ss.SSSSSSSSSXXX"

@Component({
  selector: 'hs-session-card',
  templateUrl: './session-card.component.html',
  styleUrls: ['./session-card.component.scss'],
})
export class SessionCardComponent implements OnDestroy {
  private readonly _destroyed$ = new Subject<void>()

  pinMessageTooltip?: string
  isRefreshingSession = false

  usersInSession: string[] = []
  createdDate?: Date
  reconDate?: string
  windowDate?: string
  basisName?: string
  cardTitle?: string
  acctsTooltip?: string

  deleteLoading = false
  deleteSessionId?: string
  hideCommandCount = false
  isExpanded = false
  isPlaying = false

  copyLinkIcon = 'link-variant'

  _isMaxedPinnedSessionsReached?: boolean
  @Input() set isMaxedPinnedSessionsReached(value: boolean) {
    if (value) {
      this._isMaxedPinnedSessionsReached = true
      this.pinMessageTooltip =
        'You have reached the maximum number of pinned sessions'
    } else {
      this._isMaxedPinnedSessionsReached = true
      if (this._session?.isPinned) {
        this.pinMessageTooltip = 'Pin Session to prevent autoclose'
      }
    }
  }

  _session?: SessionWithState
  @Input() set session(value: SessionWithState) {
    this._session = value
    this.usersInSession = (value?.users ?? []).map(x => x.username)
    this.updateSessionTimestamps()
    this.hideCommandCount = (this._session?.sumOfCommands ?? 0) === 0

    this.cardTitle = getSessionTitle(this._session)
    this.acctsTooltip = this._session?.loadedAccountIds.join(',')
  }

  @Output() contentDoubleClicked = new EventEmitter<void>()

  /** variables for the existing session modal */
  @Input() radioButtonsDisplay = false
  @Input() isRadioButtonSelected = false
  @Input() isActive = false

  @Input()
  @InputBoolean()
  selected = false

  @Input()
  @InputBoolean()
  selectable = false

  @Output() expanded = new EventEmitter<boolean>()

  toggleExpanded() {
    this.isExpanded = !this.isExpanded
    this.expanded.emit(this.isExpanded)
  }

  /** @internal */
  reconDateFormatter = (date: Date | undefined) =>
    date ? formatDate(date, ISODateFormat) : ''

  /** @internal */
  dateTimeFormatter = (date: Date | undefined) =>
    date
      ? formatDate(date, DisplayDateTimeSecondFormat, ISODateTimeNanoFormat)
      : ''

  /** @internal */
  @HostBinding('class.active-session')
  get activePanel() {
    return this.isActive && !this.selectable
  }

  /** @internal */
  @HostBinding('class.selected-session')
  get selectedPanel() {
    return this.selected
  }

  /** @internal */
  @HostBinding('class.session-selectable')
  get selectablePanel() {
    return this.selectable
  }

  constructor(
    private readonly sessionsService: SessionsService,
    private readonly tooltipConfig: NgbTooltipConfig,
  ) {
    this.tooltipConfig.tooltipClass = 'hs-tooltip'
    this.tooltipConfig.triggers = 'manual'
  }

  ngOnDestroy() {
    this._destroyed$.next()
    this._destroyed$.complete()
  }

  private updateSessionTimestamps() {
    if (!this._session) {
      this.createdDate = undefined
      this.reconDate = undefined
      this.windowDate = undefined
      this.basisName = undefined
      return
    }

    this.createdDate = this._session.createdDateTime
    this.reconDate =
      this.reconDateFormatter(this._session.reconDate) ?? undefined
    this.windowDate =
      this.reconDateFormatter(
        this._session.auxiliaryInfo?.LOAD_WINDOW_START_DATE,
      ) ?? undefined
    this.basisName = this._session.auxiliaryInfo?.BASIS_NAME
  }

  async copySessionLink(tooltip: NgbTooltip) {
    if (this._session) {
      await this.sessionsService.copySessionToClipboard(this._session.id)
      this.copyLinkIcon = 'check-circle'
      tooltip.open({ contents: 'Copied!' })

      // Switch it back after a period of time
      setTimeout(() => {
        this.copyLinkIcon = 'link-variant'
        tooltip.close()
      }, 1500)
    }
  }

  async pinSession() {
    if (this._session) {
      await this.sessionsService.updateSessionPinStatus(
        this._session.id,
        !this._session.isPinned,
      )
    }
  }
}
